import { useMutation } from '@tanstack/react-query';
import { useToaster } from '../../shared/utils/ToasterProvider';
import { useDialog } from '../../shared/hook/useDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import MainDialogBox from '../../shared/components/Dialog/DialogMainBox';
import { Box, Typography, CardMedia, Grid2 } from '@mui/material';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { Constants, Dashboard_URL, ImagePath, ImageRelativePath } from '../../shared/constants';
import useResponsive from '../../shared/utils/Responsive';
import useCouponStore from '../store/useCouponStore';
import useAuthStore from '../store/useAuthStore';
import useVisitedPathStore from '../store/useVisitedPathStore';
import api from '../../assets/nodeApi/axiosInstance';

export const useCoursePayment = ({
    course_id,
    course_Name,
    apartment_name,
    coverImage,
    apartments_id,
    batch_id,
    start_date,
    end_date,
    order_details,
    other_detail,
    callback_url = "collab-course-payment-callback",
    onProcessingChange,
}) => {
    const location = useLocation();
    const user = useAuthStore((state) => state.user);
    const token = useAuthStore((state) => state.accessToken);
    const { couponData } = useCouponStore();
    const [orderId, setOrderId] = useState('');
    const isSmallScreen = useResponsive("sm");
    const { showToaster } = useToaster();
    const navigate = useNavigate();

    const {
        open: openSuccessPay,
        handleClickOpen: handleOpenSuccessPay,
        handleClose: handleCloseSuccessPay
    } = useDialog();

    const createOrderMutation = useMutation({
        mutationFn: (formData) => api.post("collab-course-create-order", formData),
    });

    const handlePayment = () => {


        if (!token) {
            useVisitedPathStore.getState().setLastVisitedPath(location.pathname);
            localStorage.setItem("lastVisitedPath", location.pathname);
            localStorage.setItem("lastVisitedCourseId", course_id);
            navigate("/StudentLogin");
            return;
        }
        if (couponData?.finalPrice == 0) {
            showToaster('Warning', 'You cannot purchase courses whose price is 0.', 'warning');
            return
        }

        const obj = {
            event_id: course_id,
            price: couponData?.finalPrice,
            quantity: 1,
            apartments_id: apartments_id,
            order_details: order_details,
            details: {
                firstname: user?.firstname,
                lastname: user?.lastname,
                email: user.email
            }
        };

        createOrderMutation.mutate(obj, {
            onSuccess: (order) => {
                if (!order?.data?.payment_order_id) {
                    showToaster('Warning!', 'Failed to get order ID from server', 'warning');
                    return;
                }
                setOrderId(order?.data?.payment_order_id);
                const options = {
                    key: order?.data?.key,
                    amount: order?.data?.amount,
                    currency: "INR",
                    name: "Myskill.club",
                    description: "Payment for courses",
                    order_id: order?.data?.payment_order_id,
                    handler: async (response) => {
                        onProcessingChange?.(true);
                        const paymentData = {
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_signature: response.razorpay_signature,
                            event_id: course_id,
                            price: couponData?.finalPrice,
                            quantity: 1,
                            apartments_id: apartments_id,
                            batch_id: batch_id,
                            ...other_detail,
                            details: {
                                firstname: user?.firstname,
                                lastname: user?.lastname,
                                email: user.email,
                                course_name: course_Name,
                                start_date: start_date,
                                amount_incl_tax: couponData?.finalPrice,
                                end_date: end_date,
                                start_time: "",
                                end_time: "",
                                apartname_name: apartment_name,
                            }
                        };
                        try {
                            const verifyResponse = await api.post(callback_url, paymentData);
                         
                            // if (verifyResponse?.data?.message) {
                                handleOpenSuccessPay();
                            // } else {
                            //     showToaster('Error', "Something went wrong, Try again later", 'error');
                            // }
                        } catch (error) {
                            showToaster('Error', "Something went wrong, Try again later", 'error');
                        } finally {
                            // 🔔 notify parent → processing ended
                            onProcessingChange?.(false);
                        }
                    },
                    theme: { color: "#DC143C" }
                };
                const rzp = new window.Razorpay(options);
                rzp.open();
            },
            onError: (error) => {
                showToaster('Error', "Payment initiation failed", 'error');
            },
        });
    };

    const SuccessDialog = () => (
        <MainDialogBox open={openSuccessPay} handleClose={handleCloseSuccessPay} width={"60%"} padding={0}>
            <Box sx={{ bgcolor: "#419D27", py: 3 }}>
                <Typography variant='body1' color='primary.main' textAlign={"center"}>Payment Successful!</Typography>
            </Box>
            <Box sx={{ px: 5, py: 3 }}>
                {!isSmallScreen && (
                    <Grid2 container pb={3}>
                        <Grid2 children size={4} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <CardMedia
                                sx={{ width: "50%", borderRadius: "15px", height: "100px" }}
                                image={Constants?.customImages?.CashBackImg}
                            />
                        </Grid2>
                        <Grid2 size={8} px={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Typography variant='body2' textAlign={"center"}>
                                Thank you for your purchase. Your payment has been completed successfully.
                            </Typography>
                        </Grid2>
                    </Grid2>
                )}
                <Grid2 container pt={3}>
                    <Grid2 children size={{ xs: 12, sm: 5 }}>
                        <CardMedia
                            sx={{ width: "100%", borderRadius: "15px", height: { xs: "300px", lg: "100%" } }}
                            image={`${ImagePath}${ImageRelativePath}${coverImage}`}
                        />
                    </Grid2>
                    <Grid2 children size={{ xs: 12, sm: 7 }} pl={2}>
                        {[{ label: "Course Name", value: course_Name },
                        { label: "Order ID", value: orderId },
                        { label: "From", value: `${start_date}  To  ${end_date}` },
                        { label: "Apartment", value: apartment_name },
                        { label: "Price", value: <><CurrencyRupeeIcon fontSize="10" sx={{ mx: 0.5 }} />{couponData?.finalPrice}</> }
                        ].map(({ label, value }, idx) => (
                            <Typography key={idx} variant={idx === 0 ? "body1" : "body2"} my={1}
                                sx={label === "Price" ? { display: "flex", alignItems: "center" } : {}}>
                                {label}: {value}
                            </Typography>
                        ))}
                        <Typography
                            variant="body2"
                            fontSize={12}
                            color="#2A49F7"
                            sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                            onClick={() => window.location.href = `${Dashboard_URL}student/courses`}
                        >
                            Tap to view full details
                        </Typography>
                    </Grid2>
                </Grid2>
            </Box>
        </MainDialogBox>
    );

    return { handlePayment, SuccessDialog };
};
